import React, { useState, useEffect } from 'react';
import {
  Users,
  UserPlus,
  Check,
  Clock,
  AlertCircle,
  Filter,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Mail,
  TrendingUp,
} from 'lucide-react';
import type { TeamRecruitData } from '../types';
import { salesManagerService } from '../services/sales-manager.service';

// Funnel colors
const FUNNEL_COLORS = [
  '#3b82f6', '#8b5cf6', '#06b6d4', '#84cc16',
  '#f59e0b', '#38bdf8', '#10b981', '#14b8a6', '#22c55e',
];

const FUNNEL_STEPS = [
  'Total Invites', 'Opened Invites', 'Registered', 'Docs Signed',
  'Training Done', 'Test Passed', 'Ready to Sell', 'First Sale', 'First Paycheck',
];

export const TeamRecruitsSection: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [stepFilter, setStepFilter] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRecruits, setSelectedRecruits] = useState<Set<string>>(new Set());
  const [isSendingReminder, setIsSendingReminder] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const itemsPerPage = 10;

  const fetchRecruits = async () => {
    try {
      setIsLoading(true);
      setError('');
      const response = await salesManagerService.getTeamRecruits({
        step: stepFilter || undefined,
        status: statusFilter || undefined,
        page: currentPage,
        limit: itemsPerPage,
      });
      setRecruitData(response);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load team recruits');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRecruits();
  }, [currentPage, stepFilter, statusFilter]);

  const [recruitData, setRecruitData] = useState<TeamRecruitData | null>(null);

  const paginatedRecruits = recruitData?.recruits || [];

  const totalPages = Math.ceil((recruitData?.totalRecruits || 0) / itemsPerPage);

  const handleSelectRecruit = (recruitId: string) => {
    setSelectedRecruits((prev) => {
      const next = new Set(prev);
      if (next.has(recruitId)) {
        next.delete(recruitId);
      } else {
        next.add(recruitId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedRecruits.size === paginatedRecruits.length) {
      setSelectedRecruits(new Set());
    } else {
      setSelectedRecruits(new Set(paginatedRecruits.map((r) => r.id)));
    }
  };

  const handleSendReminder = async () => {
    if (selectedRecruits.size === 0) return;

    try {
      setIsSendingReminder(true);
      // API call would go here
      setSuccessMessage(`Reminder sent to ${selectedRecruits.size} recruit(s)`);
      setSelectedRecruits(new Set());
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err: any) {
      setError('Failed to send reminders');
    } finally {
      setIsSendingReminder(false);
    }
  };

  const getPerformanceBadge = (performance: string) => {
    switch (performance) {
      case 'overperforming':
        return (
          <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full flex items-center gap-1">
            <TrendingUp className="w-3 h-3" />
            Top Performer
          </span>
        );
      case 'inactive':
        return (
          <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Inactive
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
            Active
          </span>
        );
    }
  };

  if (isLoading && !recruitData) {
    return (
      <div className="bg-white rounded-lg shadow p-8">
        <div className="flex items-center justify-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-3 text-gray-600">Loading team recruits...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Team Recruits</h2>
        <p className="text-gray-600">View all recruits across your entire team</p>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-2">
          <Check className="w-5 h-5 text-green-600" />
          <p className="text-green-800">{successMessage}</p>
        </div>
      )}

      {/* Summary Stats */}
      {recruitData && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Team Recruits</p>
                <p className="text-2xl font-bold text-gray-900">{recruitData.totalRecruits}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <UserPlus className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Active Recruits</p>
                <p className="text-2xl font-bold text-gray-900">{recruitData.activeRecruits}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gray-100 rounded-lg">
                <Clock className="w-6 h-6 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Inactive (30-60 days)</p>
                <p className="text-2xl font-bold text-gray-900">{recruitData.inactiveRecruits}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Funnel Stats (Mini version) */}
      {recruitData && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Funnel Distribution</h3>
          <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-9 gap-2">
            {recruitData.funnelStats.map((stat) => (
              <button
                key={stat.step}
                onClick={() => setStepFilter(stepFilter === stat.step ? null : stat.step)}
                className={`p-3 rounded-lg text-center transition-all ${stepFilter === stat.step
                  ? 'ring-2 ring-blue-500 ring-offset-2'
                  : 'hover:opacity-80'
                  }`}
                style={{ backgroundColor: stat.color + '20' }}
              >
                <p className="text-2xl font-bold" style={{ color: stat.color }}>
                  {stat.count}
                </p>
                <p className="text-xs text-gray-600 truncate">{stat.name}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>
          <select
            value={stepFilter || ''}
            onChange={(e) => setStepFilter(e.target.value ? parseInt(e.target.value) : null)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Steps</option>
            {FUNNEL_STEPS.map((step, index) => (
              <option key={index + 1} value={index + 1}>
                {index + 1}. {step}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <button
            onClick={() => {
              setStepFilter(null);
              setStatusFilter('');
            }}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Clear Filters
          </button>
          <button
            onClick={handleSendReminder}
            disabled={selectedRecruits.size === 0 || isSendingReminder}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 ml-auto disabled:opacity-50"
          >
            {isSendingReminder ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Mail className="w-4 h-4" />
            )}
            Send Reminder ({selectedRecruits.size})
          </button>
        </div>
      </div>

      {/* Recruits Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedRecruits.size === paginatedRecruits.length && paginatedRecruits.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current Step</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sales</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Their Recruits</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Performance</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedRecruits.map((recruit) => (
                <tr key={recruit.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <input
                      type="checkbox"
                      checked={selectedRecruits.has(recruit.id)}
                      onChange={() => handleSelectRecruit(recruit.id)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                        {recruit.firstName[0]}{recruit.lastName[0]}
                      </div>
                      <span className="ml-3 text-sm font-medium text-gray-900">
                        {recruit.firstName} {recruit.lastName}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{recruit.email}</td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span
                      className="px-2 py-1 text-xs font-medium rounded-full text-white"
                      style={{ backgroundColor: FUNNEL_COLORS[recruit.currentFunnelStep - 1] }}
                    >
                      {recruit.stepName}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{recruit.salesCount}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{recruit.recruitCount}</td>
                  <td className="px-4 py-4 whitespace-nowrap">{getPerformanceBadge(recruit.performance)}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(recruit.joinedAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {paginatedRecruits.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-500">No recruits found for the selected filters</p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
              {Math.min(currentPage * itemsPerPage, recruitData?.totalRecruits || 0)} of{' '}
              {recruitData?.totalRecruits} recruits
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 border border-gray-300 rounded-md disabled:opacity-50 hover:bg-gray-100"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 border border-gray-300 rounded-md disabled:opacity-50 hover:bg-gray-100"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamRecruitsSection;
